import { PlotData } from "controls/plot/graph-plot/GraphPlot";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { PeakDetection } from "libraries/Math/PeakDetection";
import { Frequency } from "units/Frequency";
import { UnitValue } from "units/Unit";
import { HarmonicSelectionMode, HarmonicSelectionModes } from "./HarmonicSelectionModes";
import { SpectralMaxFinder } from "./SpectralMaxFinder";

export type Harmonic = {
    x: UnitValue;
    harmonic: number;
};

/**
 * Utility class for finding harmonic peaks in spectral data
 */
export class HarmonicPeaksFinder {
    /**
     * Find harmonic peaks based on the selected frequency and mode
     * @param spectra The spectral data to analyze
     * @param selectedFrequency The base frequency to find harmonics for
     * @param min The minimum frequency bound for searching harmonics
     * @param max The maximum frequency bound for searching harmonics
     * @param increment The increment step for harmonic numbers
     * @param harmonicSelectionMode The mode to determine how harmonics are selected
     * @returns
     */
    public static findHarmonicPeaks(
        spectra: PlotData[],
        selectedFrequency: number,
        min: number,
        max: number,
        increment: number,
        harmonicSelectionMode: HarmonicSelectionMode
    ): Harmonic[] {
        const maxLowerHarmonic = 8;
        const maxUpperHarmonic = 16;

        const harmonics = [];

        //Add higher harmonics
        for (
            let harmonic = 2;
            selectedFrequency * harmonic < max && harmonic <= maxUpperHarmonic;
            harmonic += increment
        ) {
            const harmonicFrequency = new UnitValue(selectedFrequency * harmonic, Frequency.hertz);

            if (
                harmonicSelectionMode === HarmonicSelectionModes.peak &&
                !this.isPeak(spectra, selectedFrequency, harmonic, min, max)
            )
                continue;

            harmonics.push({
                x: harmonicFrequency,
                harmonic: harmonic,
            });
        }

        // Add lower harmonics
        for (
            let harmonic = 2;
            selectedFrequency / harmonic > min && harmonic <= maxLowerHarmonic;
            harmonic += increment
        ) {
            const harmonicFrequency = new UnitValue(selectedFrequency / harmonic, Frequency.hertz);

            if (
                harmonicSelectionMode === HarmonicSelectionModes.peak &&
                !this.isPeak(spectra, selectedFrequency, 1 / harmonic, min, max)
            )
                continue;

            harmonics.push({
                x: harmonicFrequency,
                harmonic: -harmonic,
            });
        }

        return harmonics;
    }

    private static isPeak(
        spectra: PlotData[],
        originalFrequency: number,
        harmonic: number,
        min: number,
        max: number
    ): boolean {
        const harmonicFrequency = originalFrequency * harmonic;
        const searchOffsetAsHarmonicFactor = 0.4;
        let from: number, to: number;
        if (harmonic < 1) {
            from = originalFrequency / (1 / harmonic + searchOffsetAsHarmonicFactor);
            to = originalFrequency / (1 / harmonic - searchOffsetAsHarmonicFactor);
        } else {
            from = originalFrequency * (harmonic - searchOffsetAsHarmonicFactor);
            to = originalFrequency * (harmonic + searchOffsetAsHarmonicFactor);
        }

        const startRange = Math.max(from, min);
        const stopRange = Math.min(to, max);

        if (startRange >= stopRange) return false;

        return spectra.some((spectrum) => {
            const from = new UnitValue(startRange, Frequency.hertz).toUnit(spectrum.xUnit);
            const startIndex = ArrayExtensions.fastFindClosestValueIndex(spectrum.data, from, (x) => x.x);

            const to = new UnitValue(stopRange, Frequency.hertz).toUnit(spectrum.xUnit);
            const endIndex = ArrayExtensions.fastFindClosestValueIndex(spectrum.data, to, (x) => x.x);

            if (startIndex === endIndex) return false;

            // Use a smaller range for local maximum in order to avoid picking up other peaks
            const rangeRatio = 0.15;
            const localMaximumSearchStart = harmonicFrequency - (harmonicFrequency - startRange) * rangeRatio;
            const localMaximumSearchEnd = harmonicFrequency + (stopRange - harmonicFrequency) * rangeRatio;

            const localMaximumFreqHz = SpectralMaxFinder.findMaxFrequencyInRangesAsUnitValue(spectra, [
                { from: localMaximumSearchStart, to: localMaximumSearchEnd },
            ]).toUnit(Frequency.hertz);
            const localMaximumFreqXUnit = new UnitValue(localMaximumFreqHz, Frequency.hertz).toUnit(spectrum.xUnit);
            const localMaximumIndex = ArrayExtensions.fastFindClosestValueIndex(
                spectrum.data,
                localMaximumFreqXUnit,
                (x) => x.x
            );

            return PeakDetection.isPeak(spectrum.data, localMaximumIndex, {
                startIndex: startIndex,
                endIndex: endIndex,
                zScoreThreshold: 2.2,
                applyVWindow: true,
            });
        });
    }
}
